import React, { useState } from "react";
import ReactSelect from "react-select";
import { useModalKeyboardHandlers } from "../../useModalKeyboardHandlers";

const collectorTypes = [
    { label: "Flat", value: "Flat" },
    { label: "Rotating Drum", value: "Rotating Drum" },
    { label: "Rotating Mandrel", value: "Rotating Mandrel" },
    { label: "Parallel Electrodes", value: "Parallel Electrodes" },
    { label: "Wire", value: "Wire" },
    { label: "Liquid Bath", value: "Liquid Bath" },
];

const materials = [
    { label: "Aluminum", value: "Aluminum" },
    { label: "Stainless Steel", value: "Stainless Steel" },
    { label: "Copper", value: "Copper" },
    { label: "Brass", value: "Brass" },
    { label: "Nickel", value: "Nickel" },
    { label: "Tungsten", value: "Tungsten" },
    { label: "Carbon-Coated Surface", value: "Carbon-Coated Surface" },
    { label: "Glass", value: "Glass" },
    { label: "Glass (ITO Coated)", value: "Glass (ITO Coated)" },
    { label: "Plastic (Non-Conductive)", value: "Plastic (Non-Conductive)" },
    { label: "Polypropylene", value: "Polypropylene" },
    { label: "PET Film", value: "PET Film" },
    { label: "Teflon-Coated Metal", value: "Teflon-Coated Metal" },
    { label: "Silicone Tubing", value: "Silicone Tubing" },
    { label: "Copper Wire", value: "Copper Wire" },
    { label: "Stainless Steel Wire", value: "Stainless Steel Wire" },
    { label: "Nickel Wire", value: "Nickel Wire" },
    { label: "Tungsten Wire", value: "Tungsten Wire" },
    { label: "Neodymium Magnet", value: "Neodymium Magnet" },
    { label: "Acrylic (for housing or insulation)", value: "Acrylic (for housing or insulation)" }
];

const defaultCollector = {
    collectorType: "",
    collectorDefinition: {}
};

export default function CollectorModal({ collector = {}, onClose, onSave }) {
    const [localCollector, setLocalCollector] = useState({
        ...defaultCollector,
        ...collector,
        collectorDefinition: {
            ...defaultCollector.collectorDefinition,
            ...(collector?.collectorDefinition || {}),
        },
    });

    const [errors, setErrors] = useState({});

    // Handle React Select changes
    const handleSelectChange = (name, selectedOption) => {
        if (name === "collectorType") {
            setLocalCollector(prev => ({
                ...prev,
                collectorType: selectedOption ? selectedOption.value : "",
                collectorDefinition: {} // reset definition on type change
            }));
            setErrors({});
        } else if (name === "material") {
            setLocalCollector(prev => ({
                ...prev,
                collectorDefinition: {
                    ...prev.collectorDefinition,
                    material: selectedOption ? selectedOption.value : ""
                }
            }));
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setLocalCollector((prev) => ({
            ...prev,
            collectorDefinition: {
                ...prev.collectorDefinition,
                [name]: type === "number" ? (value === "" ? "" : Number(value)) : value,
            },
        }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const blockInvalidChars = (e) => {
        if (["e", "E", "-", "+"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const validate = () => {
        const { collectorType, collectorDefinition } = localCollector;
        const newErrors = {};
        const isEmpty = (val) => val === "" || val === null || val === undefined;
        const isZero = (val) => !isEmpty(val) && Number(val) === 0;
        const isNegative = (val) => !isEmpty(val) && Number(val) < 0;

        if (!collectorType) {
            newErrors.collectorType = "Collector type is required";
        }

        switch (collectorType) {
            case "Flat":
                if (!isEmpty(collectorDefinition.height)) {
                    if (isZero(collectorDefinition.height)) newErrors.height = "Height cannot be zero";
                    else if (isNegative(collectorDefinition.height)) newErrors.height = "Height must be positive";
                }

                if (!isEmpty(collectorDefinition.width)) {
                    if (isZero(collectorDefinition.width)) newErrors.width = "Width cannot be zero";
                    else if (isNegative(collectorDefinition.width)) newErrors.width = "Width must be positive";
                }
                break;
            case "Rotating Drum":
            case "Rotating Mandrel":
                if (!isEmpty(collectorDefinition.diameter)) {
                    if (isZero(collectorDefinition.diameter)) newErrors.diameter = "Diameter cannot be zero";
                    else if (isNegative(collectorDefinition.diameter)) newErrors.diameter = "Diameter must be positive";
                }

                if (!isEmpty(collectorDefinition.length)) {
                    if (isZero(collectorDefinition.length)) newErrors.length = "Length cannot be zero";
                    else if (isNegative(collectorDefinition.length)) newErrors.length = "Length must be positive";
                }

                if (isEmpty(collectorDefinition.rotationSpeed)) {
                    newErrors.rotationSpeed = "Rotation speed is required";
                } else {
                    if (isZero(collectorDefinition.rotationSpeed)) newErrors.rotationSpeed = "Rotation speed cannot be zero";
                    else if (isNegative(collectorDefinition.rotationSpeed)) newErrors.rotationSpeed = "Rotation speed must be positive";
                }
                break;
            case "Parallel Electrodes":
                if (isEmpty(collectorDefinition.electrodeGap)) newErrors.electrodeGap = "Electrode gap is required";
                else if (isZero(collectorDefinition.electrodeGap)) newErrors.electrodeGap = "Electrode gap cannot be zero";
                else if (isNegative(collectorDefinition.electrodeGap)) newErrors.electrodeGap = "Electrode gap must be positive";
                break;
            case "Wire":
                if (isEmpty(collectorDefinition.wireDiameter)) newErrors.wireDiameter = "Wire diameter is required";
                else if (isZero(collectorDefinition.wireDiameter)) newErrors.wireDiameter = "Wire diameter cannot be zero";
                else if (isNegative(collectorDefinition.wireDiameter)) newErrors.wireDiameter = "Wire diameter must be positive";

                if (isEmpty(collectorDefinition.wireLength)) newErrors.wireLength = "Wire length is required";
                else if (isZero(collectorDefinition.wireLength)) newErrors.wireLength = "Wire length cannot be zero";
                else if (isNegative(collectorDefinition.wireLength)) newErrors.wireLength = "Wire length must be positive";
                break;
            case "Liquid Bath":
                if (isEmpty(collectorDefinition.bathDepth)) newErrors.bathDepth = "Bath depth is required";
                else if (isZero(collectorDefinition.bathDepth)) newErrors.bathDepth = "Bath depth cannot be zero";
                else if (isNegative(collectorDefinition.bathDepth)) newErrors.bathDepth = "Bath depth must be positive";

                if (isEmpty(collectorDefinition.bathDiameter)) newErrors.bathDiameter = "Bath diameter is required";
                else if (isZero(collectorDefinition.bathDiameter)) newErrors.bathDiameter = "Bath diameter cannot be zero";
                else if (isNegative(collectorDefinition.bathDiameter)) newErrors.bathDiameter = "Bath diameter must be positive";
                break;
            default:
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (validate()) {
            const { collectorType, collectorDefinition } = localCollector;
            const convertNumber = (val) => (val === "" ? null : Number(val));
            let cleanedDefinition = { material: collectorDefinition.material || "" };

            // Assign specific fields based on type
            switch (collectorType) {
                case "Flat":
                    cleanedDefinition.height = convertNumber(collectorDefinition.height);
                    cleanedDefinition.width = convertNumber(collectorDefinition.width);
                    break;
                case "Rotating Drum":
                case "Rotating Mandrel":
                    cleanedDefinition.diameter = convertNumber(collectorDefinition.diameter);
                    cleanedDefinition.length = convertNumber(collectorDefinition.length);
                    cleanedDefinition.rotationSpeed = convertNumber(collectorDefinition.rotationSpeed);
                    break;
                case "Parallel Electrodes":
                    cleanedDefinition.electrodeGap = convertNumber(collectorDefinition.electrodeGap);
                    break;
                case "Wire":
                    cleanedDefinition.wireDiameter = convertNumber(collectorDefinition.wireDiameter);
                    cleanedDefinition.wireLength = convertNumber(collectorDefinition.wireLength);
                    break;
                case "Liquid Bath":
                    cleanedDefinition.bathDepth = convertNumber(collectorDefinition.bathDepth);
                    cleanedDefinition.bathDiameter = convertNumber(collectorDefinition.bathDiameter);
                    break;
                default:
                    break;
            }

            onSave({ collectorType, collectorDefinition: cleanedDefinition });
            onClose();
        }
    };

    useModalKeyboardHandlers({
        onClose,
        onSave: (e) => handleSave(e), // passed event to suppress preventDefault error if any
        dependencies: [localCollector],
    });

    const renderInput = (label, name, unit, placeholder, isRequired = true) => (
        <div className="col-md-6 mb-3">
            <label className="form-label fw-bold text-secondary">
                {label} {isRequired && <span className="text-danger">*</span>}
            </label>
            <div className="input-group">
                <input
                    type="number"
                    step="any"
                    min="0"
                    name={name}
                    className={`form-control shadow-sm ${errors[name] ? "is-invalid" : ""}`}
                    value={localCollector.collectorDefinition[name] ?? ""}
                    onChange={handleChange}
                    onKeyDown={blockInvalidChars}
                    placeholder={placeholder}
                />
                <span className="input-group-text bg-white text-muted">{unit}</span>
            </div>
            {errors[name] && <div className="text-danger small mt-1">{errors[name]}</div>}
        </div>
    );

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
                <form className="modal-content shadow-lg border-0" onSubmit={handleSave}>
                    <div className="modal-header bg-light">
                        <h5 className="modal-title fw-bold text-dark">
                            <i className="bi bi-disc me-2 text-primary"></i>
                            Edit Collector Properties
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body p-4 bg-light-subtle">
                        <div className="card shadow-sm border-light mb-4">
                            <div className="card-body p-4">
                                <div className="mb-4">
                                    <label className="form-label fw-bold text-secondary">
                                        Collector Type <span className="text-danger">*</span>
                                    </label>
                                    <ReactSelect
                                        name="collectorType"
                                        value={collectorTypes.find(c => c.value === localCollector.collectorType)}
                                        onChange={(opt) => handleSelectChange("collectorType", opt)}
                                        options={collectorTypes}
                                        placeholder="Select Type..."
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '38px',
                                                borderColor: '#dee2e6',
                                                boxShadow: 'none',
                                                '&:hover': { borderColor: '#adb5bd' }
                                            }),
                                            menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                        }}
                                        menuPortalTarget={document.body}
                                    />
                                    {errors.collectorType && <div className="text-danger small mt-1">{errors.collectorType}</div>}
                                </div>

                                {localCollector.collectorType && (
                                    <div className="row g-3 fade-in">
                                        {/* Dynamic Fields based on Type */}
                                        {localCollector.collectorType === "Flat" && (
                                            <>
                                                {renderInput("Height", "height", "cm", "Height", false)}
                                                {renderInput("Width", "width", "cm", "Width", false)}
                                            </>
                                        )}

                                        {["Rotating Drum", "Rotating Mandrel"].includes(localCollector.collectorType) && (
                                            <>
                                                {renderInput("Diameter", "diameter", "cm", "Diameter", false)}
                                                {renderInput("Length", "length", "cm", "Length", false)}
                                                {renderInput("Rotation Speed", "rotationSpeed", "rpm", "Speed")}
                                            </>
                                        )}

                                        {localCollector.collectorType === "Parallel Electrodes" && (
                                            <>
                                                {renderInput("Electrode Gap", "electrodeGap", "cm", "Gap")}
                                            </>
                                        )}

                                        {localCollector.collectorType === "Wire" && (
                                            <>
                                                {renderInput("Wire Diameter", "wireDiameter", "mm", "Diameter")}
                                                {renderInput("Wire Length", "wireLength", "cm", "Length")}
                                            </>
                                        )}

                                        {localCollector.collectorType === "Liquid Bath" && (
                                            <>
                                                {renderInput("Bath Depth", "bathDepth", "cm", "Depth")}
                                                {renderInput("Bath Diameter", "bathDiameter", "cm", "Diameter")}
                                            </>
                                        )}

                                        {/* Material Field - Always visible if type selected */}
                                        <div className="col-12 mt-3">
                                            <label className="form-label fw-bold text-secondary">Material</label>
                                            <ReactSelect
                                                name="material"
                                                value={materials.find(m => m.value === localCollector.collectorDefinition.material)}
                                                onChange={(opt) => handleSelectChange("material", opt)}
                                                options={materials}
                                                placeholder="Select Material (Optional)..."
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '38px',
                                                        borderColor: '#dee2e6',
                                                        boxShadow: 'none',
                                                        '&:hover': { borderColor: '#adb5bd' }
                                                    }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 99999 })
                                                }}
                                                menuPortalTarget={document.body}
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer bg-light border-top-0 py-3">
                        <button type="button" className="btn btn-outline-secondary px-4 rounded-pill fw-bold me-2" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary px-4 shadow-sm fw-bold rounded-pill">
                            <i className="bi bi-check2-circle me-1"></i>
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
